export type FrPrelHealthQuestionBoolean = {
  type: 'boolean'
}

export type FrPrelHealthQuestionMultiSelect = {
  type: 'multi-select'
  choices: {
    id: number
    label: string
  }[]
}

/** The "none of these" choice, which answers the question negatively. */
export const frPrelHealthQuestionNoneChoiceId = 0

export type FrPrelImcQuestion = {
  type: 'imc'
  validation: (weightKg: number, heightCm: number) => boolean
}

/** IMC (BMI) from weight in kg and height in cm. */
export function calculateImc(weightKg: number, heightCm: number): number {
  const heightM = heightCm / 100
  return weightKg / (heightM * heightM)
}

export type FrPrelHealthQuestion = {
  id: string
  label: string
  helperText?: string
  exclusionary?: boolean
} & (FrPrelHealthQuestionBoolean | FrPrelHealthQuestionMultiSelect | FrPrelImcQuestion)

export type FrPrelHealthQuestions = FrPrelHealthQuestion[]

const sportEntries = [
  ['aviation', 'Aviation'],
  ['course_automobile', 'Course automobile'],
  ['courses_bateaux_moteur', 'Courses de bateaux à moteur'],
  ['courses-motos', 'Courses de motos'],
  ['ulm', 'ULM'],
  ['parapente', 'Parapente'],
  ['deltaplane', 'Deltaplane'],
  ['vol_a_voile', 'Vol à voile'],
  ['parachutisme', 'Parachutisme'],
  ['courses_chevaux', 'Courses de chevaux'],
  ['peche_plongee_sous_marine', 'La pêche ou la plongée sous-marine avec équipement autonome'],
  ['escalade', "L'escalade (sauf sur un mur)"],
  ['saut_elastique', "Le saut à l'élastique"],
  ['speleologie', 'La spéléologie'],
  ['skeleton', 'Le skeleton'],
  ['bobsleigh', 'Le bobsleigh'],
  ['luge_competition', 'La luge de compétition'],
  ['ski_acrobatique', 'Le ski acrobatique ou le saut à ski'],
  ['varappe', 'Le varappe'],
  ['canyoning', 'Le canyoning'],
  ['rafting', 'Le rafting'],
  ['zorbing', 'Le zorbing'],
  ['randonnee_montagne', "Les randonnées en montagne ou l'alpinisme à plus de 3 000 mètres d'altitude"],
  ['sports_de_combat', 'La pratique de sports de combat'],
] as const satisfies readonly [string, string][]

export const sportList = sportEntries.map(([, label]) => label)

export const frPrelPracticedSportNoneValue = 'none'

export const frPrelPracticedSportEntries = [
  [frPrelPracticedSportNoneValue, { label: 'Aucun de cette liste' }] as const,
  ...sportEntries.map(([key, label]) => [key, { label }] as const),
] as const

export type FrPrelPracticedSport = (typeof sportEntries)[number][0]

const frPrelHealthQuestionHistoryMedicalConditions = [
  'Hypertension',
  'Hypercholestérolémie',
  'Hypertriglycéridémie',
  'Diabète',
  'Maladie cardiovasculaire',
  'Respiratoire',
  'Digestive',
  'Hépatique',
  'Urogénitale',
  'Osseuse',
  'Articulaire',
  'Affection de la colonne vertébrale (y compris lombalgies ou sciatique)',
  'Neurologique',
  'Psychique (y compris dépression ou fibromyalgie)',
  'Maladie sanguine',
  'Dermatologique',
  'Ophtalmologique ou tumorale',
] as const

export const frPrelHealthQuestionsSimplified: FrPrelHealthQuestions = [
  {
    id: '1',
    label: 'Votre IMC (indice de masse corporelle) est de :',
    type: 'imc',
    validation: (weightKg, heightCm) => {
      const imc = calculateImc(weightKg, heightCm)
      return imc < 18 || imc > 33
    },
    exclusionary: true,
  },
  {
    id: '2',
    label:
      'Etes-vous actuellement en arrêt de travail ou avez-vous eu un ou plusieurs arrêts de travail de plus de 21 jours consécutifs au cours des 10 dernières années sur prescription médicale, pour raison de santé ?',
    type: 'boolean',
  },
  {
    id: '3',
    label:
      'Suivez-vous un traitement médical ou vous a-t-on déjà prescrit un traitement médical de plus de 21 jours au cours des 10 dernières années ? (hors contraception)',
    type: 'boolean',
  },
  {
    id: '4',
    label: 'Au cours des 10 dernières années, avez-vous été hospitalisé(e) plus de 24 heures ou opéré(e) ?',
    helperText:
      "Sauf appendicite, hernie inguinale, dents de sagesse, amygdales, végétations, maternité, dons d'organe",
    type: 'boolean',
    exclusionary: true,
  },
  {
    id: '5',
    label:
      'Dans les 12 prochains mois, devez-vous être hospitalisé(e) et/ou subir une intervention chirurgicale et/ou consulter un médecin et/ou subir des examens complémentaires ?',
    type: 'boolean',
    exclusionary: true,
  },
] as const

export const frPrelHealthQuestions: FrPrelHealthQuestions = [
  {
    id: '1',
    label:
      'Etes-vous actuellement en arrêt de travail, total ou partiel, sur prescription médicale, pour raison de santé ?',
    type: 'boolean',
  },
  {
    id: '2',
    label: 'Suivez-vous actuellement un traitement médical ?',
    type: 'boolean',
  },
  {
    id: '3',
    label: 'Etes-vous titulaire de l’Allocation pour adulte handicapé (AAH) ?',
    type: 'boolean',
  },
  {
    id: '4',
    label:
      'Etes-vous titulaire d’une pension, rente ou allocation au titre d’une inaptitude au travail ou d’une invalidité ?',
    type: 'boolean',
  },
  {
    id: '5',
    label:
      'Etes-vous ou avez-vous déjà été pris en charge à 100 % pour raison médicale par un organisme de sécurité sociale au cours des 15 dernières années ?',
    type: 'boolean',
  },
] as const

export const frPrelHealthHistoryQuestions: FrPrelHealthQuestions = [
  {
    id: '1',
    label:
      'Au cours des 5 dernières années, avez-vous subi des examens, imageries médicales ou analyses de sang et/ou d’urines qui ont révélé des anomalies ?',
    type: 'boolean',
  },
  {
    id: '2',
    label:
      'Avez-vous eu un ou plusieurs arrêts de travail de plus de 21 jours consécutifs au cours des 5 dernières années sur prescription médicale pour raison de santé ?',
    type: 'boolean',
  },
  {
    id: '3',
    label: 'Vous a-t-on déjà prescrit un traitement médical de plus de 21 jours au cours des 5 dernières années ?',
    type: 'boolean',
  },
  {
    id: '4',
    label:
      'Avez-vous subi un test de dépistage des sérologies portant en particulier sur les virus des hépatites B et C sur celui de l’immunodéficience humaine (HIV) dont le résultat a été positif ?',
    type: 'boolean',
  },
  {
    id: '5',
    label: 'Etes-vous ou avez-vous été atteint(e) d’une ou plusieurs affections médicales',
    type: 'multi-select',
    choices: [
      {
        id: frPrelHealthQuestionNoneChoiceId,
        label: 'Aucun de ces choix',
      },
      ...frPrelHealthQuestionHistoryMedicalConditions.map((label, index) => ({
        id: index + 1,
        label,
      })),
    ],
  },
] as const

export type FrPrelHealthQuestionsFormType = 'simplified' | 'full' | 'history'

export const frPrelHealthQuestionsFormByType: Record<FrPrelHealthQuestionsFormType, FrPrelHealthQuestions> = {
  simplified: frPrelHealthQuestionsSimplified,
  full: frPrelHealthQuestions,
  history: frPrelHealthHistoryQuestions,
}
